import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Container, Card, CardContent, Typography, Button, CircularProgress, Alert, Stack,
} from '@mui/material';
import useAuth from '../hooks/useAuth';
import { getInviteDetails, acceptInvite, declineInvite } from '../services/organizationService';

export default function InviteAcceptPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading: authLoading, refreshUser } = useAuth();
  const [invite, setInvite] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionDone, setActionDone] = useState('');

  useEffect(() => {
    getInviteDetails(token)
      .then(setInvite)
      .catch((err) => setError(err.response?.data?.message || 'Invite not found or expired'))
      .finally(() => setIsLoading(false));
  }, [token]);

  async function handleAccept() {
    setError('');
    try {
      await acceptInvite(token);
      await refreshUser();
      setActionDone('accepted');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to accept invite');
    }
  }

  async function handleDecline() {
    setError('');
    try {
      await declineInvite(token);
      setActionDone('declined');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to decline invite');
    }
  }

  if (isLoading || authLoading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <>
      <Helmet><title>VPSI | Organization Invite</title></Helmet>
      <Container maxWidth="sm" sx={{ mt: 10 }}>
        <Card>
          <CardContent>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {actionDone === 'accepted' && (
              <>
                <Alert severity="success" sx={{ mb: 2 }}>You have joined the organization.</Alert>
                <Button variant="contained" onClick={() => navigate('/dashboard/account')}>
                  Go to Account
                </Button>
              </>
            )}

            {actionDone === 'declined' && (
              <Alert severity="info">Invite declined.</Alert>
            )}

            {!actionDone && invite && !isAuthenticated && (
              <>
                <Typography variant="h5" gutterBottom>Organization Invite</Typography>
                <Typography paragraph>
                  You have been invited to join <strong>{invite.organizationName}</strong> as a <strong>{invite.role}</strong>.
                </Typography>
                <Typography paragraph>Please log in or sign up to accept this invite.</Typography>
                <Button
                  variant="contained"
                  onClick={() => navigate(`/login?redirect=/invite/${token}`)}
                >
                  Log In / Sign Up
                </Button>
              </>
            )}

            {!actionDone && invite && isAuthenticated && (
              <>
                <Typography variant="h5" gutterBottom>Organization Invite</Typography>
                <Typography paragraph>
                  You have been invited to join <strong>{invite.organizationName}</strong> as a <strong>{invite.role}</strong>.
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Logged in as {user?.email}
                </Typography>
                <Stack direction="row" spacing={2}>
                  <Button variant="contained" color="primary" onClick={handleAccept}>
                    Accept Invite
                  </Button>
                  <Button variant="outlined" color="inherit" onClick={handleDecline}>
                    Decline
                  </Button>
                </Stack>
              </>
            )}

            {!actionDone && !invite && !error && (
              <Typography>This invite is no longer valid.</Typography>
            )}
          </CardContent>
        </Card>
      </Container>
    </>
  );
}
